import { useEffect, useRef, useState } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useLocale } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { SERVICE_DESK } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { calculateGpEndDate, getGpEndDateDetails } from './utils.tsx';

// Module-level cache for the GP end date
let gpEndDateCache:
	| {
			data: { gpEndDate: string | undefined };
			loading: boolean;
			error: Error | unknown | null;
			subscribers: Set<
				(result: {
					gpEndDate: string | undefined;
					loading: boolean;
					error: Error | unknown | null;
				}) => void
			>;
			abortController?: AbortController;
	  }
	| undefined;

/**
 * Clear the GP end date cache - primarily for testing purposes
 */
export const clearGpEndDateCache = () => {
	// Cancel any ongoing request when clearing cache
	if (gpEndDateCache?.abortController) {
		gpEndDateCache.abortController.abort();
	}
	gpEndDateCache = undefined;
};

export const useDisplayGpEndDate = () => {
	const [loading, setLoading] = useState(true);
	const [gpEndDate, setGpEndDate] = useState<string | undefined>(undefined);
	const [error, setError] = useState<Error | unknown | null>(null);

	const { isAdmin, productEntitlementDetails } = useTenantContext();
	const entitlementId = productEntitlementDetails?.[SERVICE_DESK]?.entitlementId;

	const locale = useLocale();
	const subscriberRef = useRef<
		| ((result: {
				gpEndDate: string | undefined;
				loading: boolean;
				error: Error | unknown | null;
		  }) => void)
		| null
	>(null);
	const isMountedRef = useRef<boolean>(true);

	useEffect(() => {
		isMountedRef.current = true;

		// Create subscriber function for this hook instance
		const subscriber = (result: {
			gpEndDate: string | undefined;
			loading: boolean;
			error: Error | unknown | null;
		}) => {
			// Only update state if component is still mounted
			if (isMountedRef.current) {
				setGpEndDate(result.gpEndDate);
				setLoading(result.loading);
				setError(result.error);
			}
		};
		subscriberRef.current = subscriber;

		// If we have cached data, use it immediately
		if (gpEndDateCache) {
			subscriber({
				gpEndDate: gpEndDateCache.data.gpEndDate,
				loading: gpEndDateCache.loading,
				error: gpEndDateCache.error,
			});

			// Add this subscriber to the set
			gpEndDateCache.subscribers.add(subscriber);

			return () => {
				isMountedRef.current = false;
				// Clean up subscriber on unmount
				if (gpEndDateCache) {
					gpEndDateCache.subscribers.delete(subscriber);
				}
			};
		}

		// Initialize cache and start the request
		const abortController = new AbortController();
		gpEndDateCache = {
			data: { gpEndDate: undefined },
			loading: true,
			error: null,
			subscribers: new Set([subscriber]),
			abortController,
		};

		const loadGpEndDate = async () => {
			try {
				// Check if request was aborted before proceeding
				if (abortController.signal.aborted) {
					return;
				}

				const gpEndDateDetails = await getGpEndDateDetails(isAdmin, entitlementId);

				// Check abort after async operation
				if (abortController.signal.aborted) {
					return;
				}

				const calculatedGpEndDateValue = calculateGpEndDate(
					gpEndDateDetails.maintenanceEndDate,
					gpEndDateDetails.billingPeriod,
					locale,
				);

				// Update cache and notify all subscribers
				if (gpEndDateCache && !abortController.signal.aborted) {
					gpEndDateCache.data.gpEndDate = calculatedGpEndDateValue;
					gpEndDateCache.loading = false;
					gpEndDateCache.error = null;

					// Only notify subscribers that are still mounted
					gpEndDateCache.subscribers.forEach((sub) => {
						sub({
							gpEndDate: calculatedGpEndDateValue,
							loading: false,
							error: null,
						});
					});
				}
			} catch (e) {
				// Update cache with error and notify all subscribers
				if (gpEndDateCache && !abortController.signal.aborted) {
					gpEndDateCache.data.gpEndDate = undefined;
					gpEndDateCache.loading = false;
					gpEndDateCache.error = e;

					gpEndDateCache.subscribers.forEach((sub) => {
						sub({
							gpEndDate: undefined,
							loading: false,
							error: e,
						});
					});
				}

				fireErrorAnalytics({
					meta: {
						id: 'jiraServicedeskJsmContextResource',
						packageName: 'premiumLandingPage',
						teamName: 'Sequoia',
					},
					error: e instanceof Error ? e : new Error(String(e)),
				});
			}
		};

		loadGpEndDate();

		return () => {
			isMountedRef.current = false;
			// Cancel the request if it's still ongoing
			abortController.abort();
			// Clean up subscriber on unmount
			if (gpEndDateCache && subscriberRef.current) {
				gpEndDateCache.subscribers.delete(subscriberRef.current);
			}
		};
	}, [locale, entitlementId, isAdmin]);

	return { loading, gpEndDate };
};
